import { authManager } from '../auth-manager.js';
import {
  getDB,
  saveShoppingListNotaLinkLocal,
  deleteShoppingListNotaLinkLocal,
  getShoppingListNotasLocal,
  getNotasShoppingListsLocal,
  getListasLocal,
  recalculateListaTotalsLocal
} from '../db.js';
import { escapeHTML } from '../security.js';

/**
 * Calculates temporal proximity score between an NFC-e note and a shopping list.
 * @param {string|Date} notaDate 
 * @param {string|Date} listDate 
 * @returns {{ score: number, timeDiffStr: string, isWithinWindow: boolean }}
 */
export function calculateTemporalProximity(notaDate, listDate) {
  const nTime = new Date(notaDate || Date.now()).getTime();
  const lTime = new Date(listDate || Date.now()).getTime();
  const diffMs = Math.abs(nTime - lTime);
  const diffHours = diffMs / (1000 * 60 * 60);

  let timeDiffStr = `${Math.round(diffHours)}h atrás`;
  if (diffHours < 1) {
    const diffMinutes = Math.max(1, Math.round(diffMs / (1000 * 60)));
    timeDiffStr = `${diffMinutes}m atrás`;
  } else if (diffHours > 24) {
    const diffDays = Math.round(diffHours / 24);
    timeDiffStr = `${diffDays}d atrás`;
  }

  // 12-hour high-confidence window, decaying up to 48 hours
  const isWithinWindow = diffHours <= 48;
  let score = 1.0 - (diffHours / 48) * 0.5;
  if (score < 0.5) score = 0.5;
  if (diffHours > 48) score = 0.2;

  return {
    score: Number(score.toFixed(2)),
    timeDiffStr,
    isWithinWindow
  };
}

/**
 * Returns candidate open shopping lists for a note, sorted by proximity score.
 */
export async function getSuggestedShoppingLists(nota, scope = 'personal') {
  if (!nota) return [];

  // Try API first if online
  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth(`/api/notas/${nota.id}/suggested-lists?scope=${scope}`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          return data;
        }
      }
    } catch (e) {
      console.warn('API getSuggestedShoppingLists fallback to local:', e);
    }
  }

  // Local fallback
  try {
    const allLists = await getListasLocal(scope);
    const openLists = allLists.filter(l => (l.status === 'aberta' || !l.status));
    const linkedLinks = await getNotasShoppingListsLocal(nota.id);
    const linkedListIds = new Set(linkedLinks.map(l => Number(l.shoppingListId)));

    const candidates = [];
    const notaTime = nota.dataEmissao || nota.createdAt || new Date();

    for (const list of openLists) {
      if (linkedListIds.has(Number(list.id))) continue;

      const listTime = list.updatedAt || list.createdAt || new Date();
      const { score, timeDiffStr, isWithinWindow } = calculateTemporalProximity(notaTime, listTime);

      if (isWithinWindow) {
        candidates.push({
          list,
          confidenceScore: score,
          timeDifference: timeDiffStr
        });
      }
    }

    return candidates.sort((a, b) => b.confidenceScore - a.confidenceScore);
  } catch (e) {
    console.error('getSuggestedShoppingLists error:', e);
    return [];
  }
}

/**
 * Links a Nota Fiscal to a Shopping List.
 */
export async function linkNotaToShoppingList(listId, notaId, matchedVia = 'manual_card', confidenceScore = 1.0, allocatedAmount = null) {
  let backendSuccess = false;
  let updatedList = null;

  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth(`/api/listas/${listId}/notas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notaId: Number(notaId),
          matchedVia,
          confidenceScore,
          allocatedAmount
        })
      });
      if (res.ok) {
        updatedList = await res.json();
        backendSuccess = true;
      }
    } catch (e) {
      console.warn('Backend linkNotaToShoppingList failed, queuing locally:', e);
    }
  }

  // Update local IndexedDB
  await saveShoppingListNotaLinkLocal({
    shoppingListId: Number(listId),
    notaId: Number(notaId),
    matchedVia,
    confidenceScore,
    allocatedAmount
  });

  const db = await getDB();
  const localList = await db.get('listas', Number(listId));
  if (localList) {
    localList.updatedAt = new Date().toISOString();
    await db.put('listas', localList);
    await recalculateListaTotalsLocal(Number(listId));
  }

  return updatedList || localList;
}

/**
 * Unlinks a Nota Fiscal from a Shopping List.
 */
export async function unlinkNotaFromShoppingList(listId, notaId) {
  if (authManager.isAuthenticated()) {
    try {
      await authManager.fetchWithAuth(`/api/listas/${listId}/notas/${notaId}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.warn('Backend unlinkNotaFromShoppingList failed:', e);
    }
  }

  await deleteShoppingListNotaLinkLocal(Number(listId), Number(notaId));
  await recalculateListaTotalsLocal(Number(listId));
}

/**
 * Retrieves all shopping lists linked to a given note.
 */
export async function getLinkedListsForNota(notaId) {
  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth(`/api/notas/${notaId}/listas`);
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {}
  }

  // Local fallback
  const links = await getNotasShoppingListsLocal(Number(notaId));
  const lists = [];
  const db = await getDB();
  for (const link of links) {
    const list = await db.get('listas', Number(link.shoppingListId));
    if (list) lists.push(list);
  }
  return lists;
}

/**
 * Interactive Modal for Linking a Nota Fiscal to Shopping Lists
 */
export function openLinkToListModal({ nota, onLinked }) {
  const existingModal = document.getElementById('link-nota-modal');
  if (existingModal) existingModal.remove();

  const storeName = nota.emitente?.razaoSocial || nota.razaoSocial || 'Nota Fiscal';
  const valorTotal = (nota.totais?.valorTotal || nota.valorTotal || 0).toFixed(2).replace('.', ',');

  const modalHtml = `
    <div id="link-nota-modal" class="modal-overlay" style="display: flex; align-items: center; justify-content: center; position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 10000; padding: 1rem; backdrop-filter: blur(4px);">
      <div class="modal-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1.25rem; max-width: 480px; width: 100%; max-height: 90vh; display: flex; flex-direction: column; box-shadow: var(--shadow-card); overflow: hidden;">
        
        <!-- Header -->
        <div style="padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border-color); display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 0.6rem;">
            <div style="width: 38px; height: 38px; border-radius: 0.65rem; background: rgba(37,99,235,0.12); color: #2563EB; display: flex; align-items: center; justify-content: center;">
              <span class="material-symbols-outlined" style="font-size: 22px;">link</span>
            </div>
            <div>
              <h3 style="margin: 0; font-size: 1.1rem; font-weight: 800; color: var(--text-main);">Vincular à Lista</h3>
              <p style="margin: 0.15rem 0 0 0; font-size: 0.8rem; color: var(--text-muted); font-weight: 500;">${escapeHTML(storeName)} &bull; R$ ${valorTotal}</p>
            </div>
          </div>
          <button id="btn-close-link-modal" style="background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0.4rem; border-radius: 0.4rem; display: flex; align-items: center;">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <!-- Search Input -->
        <div style="padding: 0.85rem 1.5rem; border-bottom: 1px solid var(--border-color); background: var(--bg-surface-alt);">
          <div style="position: relative;">
            <span class="material-symbols-outlined" style="position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); font-size: 18px; color: var(--text-muted);">search</span>
            <input type="text" id="link-modal-search" placeholder="Buscar lista aberta..." style="width: 100%; box-sizing: border-box; padding: 0.6rem 0.75rem 0.6rem 2.4rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 0.65rem; color: var(--text-main); font-size: 0.875rem; outline: none;">
          </div>
        </div>

        <!-- List Selection Container -->
        <div id="link-modal-lists" style="padding: 1rem 1.5rem; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 0.6rem; background: var(--bg-surface);">
          <div style="display: flex; align-items: center; justify-content: center; padding: 2rem; color: var(--text-muted);">
            <span class="material-symbols-outlined spin" style="margin-right: 0.5rem;">sync</span> Carregando listas...
          </div>
        </div>

        <!-- Footer -->
        <div style="padding: 1rem 1.5rem; border-top: 1px solid var(--border-color); display: flex; justify-content: flex-end; gap: 0.75rem; background: var(--bg-surface-alt);">
          <button id="btn-cancel-link-modal" class="btn btn-secondary" style="padding: 0.5rem 1.2rem; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.85rem;">Cancelar</button>
        </div>

      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  const modalEl = document.getElementById('link-nota-modal');
  const listsContainer = document.getElementById('link-modal-lists');
  const searchInput = document.getElementById('link-modal-search');
  const btnClose = document.getElementById('btn-close-link-modal');
  const btnCancel = document.getElementById('btn-cancel-link-modal');

  const closeModal = () => modalEl.remove();
  btnClose.addEventListener('click', closeModal);
  btnCancel.addEventListener('click', closeModal);
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) closeModal();
  });

  // Load lists
  (async () => {
    try {
      const [personalLists, familyLists, linkedLists] = await Promise.all([
        getListasLocal('personal').catch(() => []),
        getListasLocal('family').catch(() => []),
        getLinkedListsForNota(nota.id).catch(() => [])
      ]);

      const linkedIds = new Set(linkedLists.map(l => Number(l.id)));
      const allOpen = [...personalLists, ...familyLists].filter(l => (l.status === 'aberta' || !l.status));

      // Remove duplicate IDs
      const uniqueOpenMap = new Map();
      allOpen.forEach(l => uniqueOpenMap.set(Number(l.id), l));
      const openLists = Array.from(uniqueOpenMap.values());

      function renderListItems(query = '') {
        const q = query.toLowerCase().trim();
        const filtered = openLists.filter(l => (l.name || '').toLowerCase().includes(q));

        if (filtered.length === 0) {
          listsContainer.innerHTML = `
            <div style="text-align: center; padding: 2rem 1rem; color: var(--text-muted);">
              <span class="material-symbols-outlined" style="font-size: 2.5rem; margin-bottom: 0.5rem; opacity: 0.5;">checklist</span>
              <p style="margin: 0; font-weight: 600; font-size: 0.9rem; color: var(--text-main);">Nenhuma lista aberta encontrada.</p>
              <a href="#listas" style="display: inline-block; margin-top: 0.75rem; font-size: 0.85rem; font-weight: 700; color: #2563EB;">Criar nova lista de compras</a>
            </div>
          `;
          return;
        }

        listsContainer.innerHTML = filtered.map(l => {
          const isLinked = linkedIds.has(Number(l.id));
          const { score, timeDiffStr } = calculateTemporalProximity(nota.dataEmissao, l.updatedAt || l.createdAt);
          const isHighSuggestion = score >= 0.8 && !isLinked;
          const isFamily = l.scope === 'family' || Boolean(l.familyId);

          return `
            <div class="link-item-row" data-list-id="${l.id}" style="display: flex; align-items: center; justify-content: space-between; padding: 0.85rem 1rem; background: ${isLinked ? 'rgba(16, 185, 129, 0.08)' : 'var(--bg-surface-alt)'}; border: 1px solid ${isLinked ? 'rgba(16, 185, 129, 0.35)' : 'var(--border-color)'}; border-radius: 0.85rem; transition: all 0.15s ease;">
              <div style="flex: 1; margin-right: 0.75rem; min-width: 0;">
                <div style="display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.2rem; flex-wrap: wrap;">
                  <span style="font-weight: 700; color: var(--text-main); font-size: 0.95rem;">${escapeHTML(l.name)}</span>
                  ${isFamily ? `<span style="font-size: 0.68rem; font-weight: 800; background: rgba(99, 102, 241, 0.15); color: #6366F1; border: 1px solid rgba(99, 102, 241, 0.25); padding: 0.05rem 0.4rem; border-radius: var(--radius-pill);">Família</span>` : ''}
                  ${isHighSuggestion ? `<span style="font-size: 0.68rem; font-weight: 800; background: rgba(245, 158, 11, 0.15); color: #D97706; border: 1px solid rgba(245, 158, 11, 0.3); padding: 0.05rem 0.4rem; border-radius: var(--radius-pill);">💡 Sugerida (${timeDiffStr})</span>` : ''}
                </div>
                <div style="font-size: 0.78rem; color: var(--text-muted); font-weight: 500;">
                  ${(l.items || []).length || l.itemCount || 0} itens &bull; Estimado: R$ ${(l.totalEstimated || 0).toFixed(2).replace('.', ',')}
                </div>
              </div>
              <div style="flex-shrink: 0;">
                ${isLinked ? `
                  <button class="btn btn-unlink-item" data-list-id="${l.id}" style="padding: 0.35rem 0.75rem; background: rgba(239, 68, 68, 0.1); color: #EF4444; border: 1px solid rgba(239, 68, 68, 0.25); border-radius: var(--radius-pill); font-size: 0.78rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 0.25rem;">
                    <span class="material-symbols-outlined" style="font-size: 15px;">link_off</span> Desvincular
                  </button>
                ` : `
                  <button class="btn btn-link-item" data-list-id="${l.id}" style="padding: 0.35rem 0.85rem; background: #2563EB; color: #FFFFFF; border: none; border-radius: var(--radius-pill); font-size: 0.78rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 0.25rem;">
                    <span class="material-symbols-outlined" style="font-size: 15px;">link</span> Vincular
                  </button>
                `}
              </div>
            </div>
          `;
        }).join('');

        // Attach buttons listeners
        listsContainer.querySelectorAll('.btn-link-item').forEach(btn => {
          btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const listId = btn.getAttribute('data-list-id');
            btn.disabled = true;
            btn.innerHTML = `<span class="material-symbols-outlined spin" style="font-size: 16px;">sync</span> Vinculando...`;
            await linkNotaToShoppingList(listId, nota.id, 'manual_card', 1.0);
            closeModal();
            if (onLinked) onLinked();
          });
        });

        listsContainer.querySelectorAll('.btn-unlink-item').forEach(btn => {
          btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const listId = btn.getAttribute('data-list-id');
            btn.disabled = true;
            btn.innerHTML = `<span class="material-symbols-outlined spin" style="font-size: 16px;">sync</span> Removendo...`;
            await unlinkNotaFromShoppingList(listId, nota.id);
            closeModal();
            if (onLinked) onLinked();
          });
        });
      }

      renderListItems();

      searchInput.addEventListener('input', (e) => {
        renderListItems(e.target.value);
      });
    } catch (e) {
      listsContainer.innerHTML = `
        <div style="text-align: center; padding: 2rem 1rem; color: #EF4444;">
          Erro ao carregar listas: ${escapeHTML(e.message)}
        </div>
      `;
    }
  })();
}

/**
 * Calculates budget accuracy, deviation, and variance metrics for shopping lists with linked notes.
 * @param {Array} shoppingLists 
 * @returns {Object}
 */
export function calculateBudgetReconciliationAnalytics(shoppingLists = []) {
  const reconciledLists = (shoppingLists || []).filter(l => 
    (l.linkedNotas && l.linkedNotas.length > 0) || (l.totalActual || l.totalReal || 0) > 0
  );

  let totalEstimated = 0;
  let totalActual = 0;
  let withinBudgetCount = 0;
  let overBudgetCount = 0;

  const items = reconciledLists.map(l => {
    const estimated = Number(l.totalEstimated || l.totalEstimado || 0);
    const linked = l.linkedNotas || [];
    const actual = linked.length > 0
      ? linked.reduce((sum, ln) => sum + Number(ln.allocatedAmount || ln.nota?.totais?.valorTotal || ln.nota?.valorTotal || 0), 0)
      : Number(l.totalActual || l.totalReal || 0);

    totalEstimated += estimated;
    totalActual += actual;

    const diff = actual - estimated;
    const diffPct = estimated > 0 ? (diff / estimated) * 100 : 0;
    const isOver = diff > 0.01;
    const accuracy = estimated > 0 ? Math.max(0, Math.min(100, 100 - Math.abs(diffPct))) : 100;

    if (isOver) {
      overBudgetCount++;
    } else {
      withinBudgetCount++;
    }

    return {
      id: l.id,
      name: l.name || l.nome || 'Lista de Compras',
      type: l.type || l.tipo || 'semanal',
      scope: l.scope || 'personal',
      monthRef: l.monthRef || l.mesRef || 'Geral',
      createdAt: l.createdAt || l.dataCriacao,
      estimated,
      actual,
      diff,
      diffPct,
      accuracy,
      isOver,
      linkedNotas: linked
    };
  });

  const totalDiff = totalActual - totalEstimated;
  const totalDiffPct = totalEstimated > 0 ? (totalDiff / totalEstimated) * 100 : 0;
  const globalAccuracyPct = totalEstimated > 0 
    ? Math.max(0, Math.min(100, Math.round(100 - Math.abs(totalDiffPct)))) 
    : 100;

  return {
    totalEstimated,
    totalActual,
    totalDiff,
    totalDiffPct,
    globalAccuracyPct,
    withinBudgetCount,
    overBudgetCount,
    reconciledCount: reconciledLists.length,
    items
  };
}

